import React, { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { BsPersonBoundingBox } from "react-icons/bs";
import { LuBaby } from "react-icons/lu";
import { FaCheckDouble, FaUserLock } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import moment from "moment";
import "moment-hijri";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import CardHeading from "../../../shared/CardHeading";
import SwitchButtons from "../../../shared/SwitchButtons";
import TextInput from "../../../shared/TextInput";
import DatePicker from "../../../shared/DatePicker";
import DropdownField from "../../../shared/DropdownField";
import SwitchInput from "../../../shared/SwitchInput";
import AutoCompleteField from "../../../shared/AutoCompleteField";
import CustomButton from "../../../shared/CustomButton";
import InsurancePlansForm from "./InsurancePlansForm";
import {
  citiesOptions,
  documentTypeOptions,
  genderOptions,
  maritalStatusOptions,
  nationalitiesOptions,
  occupationOptions,
  patientInsurancePlanOptions,
  patientOptionsFormat,
  religionOptions,
  residencyTypesOptions,
  subscriberRelationsOptions,
  visaTypeOptions,
} from "../../../constants/BeneficiaryConstants";
import {
  useCreatePatientMutation,
  useUpdatePatientMutation,
  useLazyGetPatientByIdQuery,
  useGetPatientRecordSummaryMutation,
} from "../../../redux/api/modules/beneficiaryApi";
import { handleCheckPatientElibility } from "../../../services/ApiMethods";
import { getPayersList } from "../../../redux/slices/VisitsSlice";
import BeneFiciaryGuarantorForm from "./BeneFiciaryGuarantorForm";
import BeneficiaryEmergencyContact from "./BeneficiaryEmergencyContact";
import CheckPermission from "../../../hooks/CheckPermission";
import { getDateMinusThreeMonths } from "../../../utils/FormateDateTime";
import { BeneficiaryValidationSchema } from "../../../validations/BeneficiaryFormValidations";
import PasswordField from "../../../shared/PasswordField";
import ReusableModal from "../../../shared/ReuseableModal";
import { FaShield } from "react-icons/fa6";
import EligibilityDetailComp from "./EligibilityDetailComp";
import HijriDatePicker from "../../../shared/HijriDatePicker";
import PatientRecordSummaryModal from "./PatientRecordSummaryModal";

const cleanName = (name) => {
  if (!name) return "";
  return name.replace(/[^a-zA-Z\u0600-\u06FF\s]/g, "").trim().replace(/\s+/g, " ");
};

const normalizePayerName = (name = "") =>
  name
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/\b(the|co-?operative|insurance|company|for)\b/g, "")
    .replace(/[^a-z0-9\u0600-\u06FF]/g, "");

const payerNamesMatch = (payer, insurance) => {
  const payerNameEN = (payer?.name_en || payer?.name || "").replace(/\*/g, "");
  const payerNameAR = (payer?.name_ar || "").replace(/\*/g, "");
  const insuranceNameEN =
    insurance?.InsuranceCompanyEN || insurance?.InsuranceCompany || "";
  const insuranceNameAR =
    insurance?.InsuranceCompanyAR || insurance?.ArabicICName || "";
  const normalizedPayerNameEN = normalizePayerName(payerNameEN);
  const normalizedInsuranceNameEN = normalizePayerName(insuranceNameEN);
  const normalizedPayerNameAR = normalizePayerName(payerNameAR);
  const normalizedInsuranceNameAR = normalizePayerName(insuranceNameAR);

  return (
    (insuranceNameEN && payerNameEN === insuranceNameEN) ||
    (insuranceNameAR && payerNameAR === insuranceNameAR) ||
    (normalizedPayerNameEN &&
      normalizedInsuranceNameEN &&
      normalizedPayerNameEN === normalizedInsuranceNameEN) ||
    (normalizedPayerNameAR &&
      normalizedInsuranceNameAR &&
      normalizedPayerNameAR === normalizedInsuranceNameAR)
  );
};

const getSubscriberInsurancePlanId = (value) =>
  Array.isArray(value) ? value.flat(Infinity)[0] || "" : value || "";

const getPatientInsurancePlans = (patient) =>
  patient?.insurance_plans || patient?.Plan || [];

const getBeneficiaryInsurancePlans = (details) => {
  const plans = details?.insurance_plans || [];
  if (!details?.is_new_born) return plans;

  const subscriberPlanId = getSubscriberInsurancePlanId(
    details?.subscriber_insurance_plan
  );
  if (!subscriberPlanId) return plans.slice(0, 1);

  const selectedPlan = plans.find(
    (plan) =>
      String(plan?.insurance_plan_id || "") === String(subscriberPlanId) ||
      String(plan?.his_payer_id || plan?.payer_id || "") ===
        String(subscriberPlanId)
  );

  return selectedPlan ? [selectedPlan] : plans.slice(0, 1);
};

const getSubscriberInsurancePlans = (details) =>
  details?.subscriber_insurance_plans ||
  details?.subscriber?.insurance_plans ||
  details?.subscriber?.Plan ||
  getBeneficiaryInsurancePlans(details);

const isBlankInsurancePlanValue = (value) =>
  value === undefined || value === null || String(value).trim() === "";

const getPlanClassType = (plan) => plan?.class_type || "plan";

const getPolicyHolder = (plan) =>
  plan?.policy_holder ||
  plan?.PolicyHolder ||
  plan?.policyHolder ||
  plan?.holder_name ||
  plan?.payer_name ||
  "";

const normalizeInsurancePlan = (plan) => ({
  ...plan,
  policy_holder: getPolicyHolder(plan),
  class_type: getPlanClassType(plan),
  policy_class_type: getPlanClassType(plan),
});

const getMissingInsurancePlanFields = (plan, benificiaryType) => {
  const planWithClassType = normalizeInsurancePlan(plan || {});
  const requiredFields = [
    ["member_card_id", "Member Card ID"],
    ["issue_date", "Issue Date"],
    ["max_limit", "Max Limit"],
    ["patient_share", "Patient Share"],
    ["relation_with_subscriber", "Relation with subscriber"],
    ["class_type", "Class Type"],
  ];

  if (benificiaryType === "insurance") {
    requiredFields.push(
      ["policy_number", "Policy number"],
      ["policy_holder", "Policy holder"],
      ["class_id", "Class ID"]
    );
  }

  if (benificiaryType !== "corporate") {
    requiredFields.splice(2, 0, ["expiry_date", "Expiry date"]);
  }

  if (benificiaryType === "corporate") {
    return [];
  }

  return requiredFields
    .filter(([field]) => isBlankInsurancePlanValue(planWithClassType?.[field]))
    .map(([, label]) => label);
};

const getSubscriberOption = (details) => {
  if (!details?.subscriber_id) return "";

  const fullName = details?.subscriber_full_name || "";
  const documentId = details?.subscriber_document_id || "";
  const fileNo = details?.subscriber_file_no || "";
  const labelParts = [fullName, documentId, fileNo].filter(Boolean);

  return {
    label: labelParts.join(" --- ") || String(details.subscriber_id),
    value: details.subscriber_id,
    full_name: fullName,
    file_no: fileNo,
    document_id: documentId,
    rcm_ref: details?.subscriber_rcm_ref || "",
    beneficiary_type:
      details?.subscriber_beneficiary_type?.toLowerCase() || "cash",
    insurance_plans: details?.subscriber_insurance_plans || [],
    nationality: details?.subscriber_nationality || details?.subscriber?.nationality || "",
    city: details?.subscriber_city || details?.subscriber?.city || "",
    address: details?.subscriber_address || details?.subscriber?.address || "",
    religion: details?.subscriber_religion || details?.subscriber?.religion || "",
  };
};

const getBabyName = (subscriberName) =>
  subscriberName ? `Baby of ${subscriberName}` : "";

const BeneficiaryPersonalDataForm = ({
  beneficiaryDetails,
  getBeneficiariesPayload,
  cchiSearchData,
  loading,
  beneficiaryDetailsFromAppointment,
  returnDetail,
  fromVisit,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const editForm = location?.pathname == "/edit-beneficiary";
  const isInpatientCheckInUpdate =
    editForm && fromVisit && returnDetail?.tab === "inpatient";
  const shouldShowInPatientSwitch = !beneficiaryDetails || editForm;
  const { hasPermission } = CheckPermission();
  const [GenderValue, setGender] = useState(
    beneficiaryDetails?.gender || "male"
  );
  const isHaveGetPayerPermission = hasPermission("payer", "get");
  const { payersData, isLoading: isLoadingPayers } = useSelector(
    (state) => state?.visits
  );
  const auth = useSelector((state) => state?.auth);

  const [benificiaryType, setBenificiaryType] = useState(
    beneficiaryDetails?.beneficiary_type?.toLowerCase() || "cash"
  );
  const [isNewBorn, setIsNewBorn] = useState(
    beneficiaryDetails?.is_new_born || false
  );
  const [isUserLogin, setIsUserLogin] = useState(
    beneficiaryDetails?.user_login || false
  );
  const [isInPatient, setIsInPatient] = useState(
    Boolean(beneficiaryDetails?.is_in_patient || isInpatientCheckInUpdate)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdateEpisode, setIsUpdateEpisode] = useState(false);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [selectedPatientInsurancePlans, setSelectedPatientInsurancePlans] =
    useState(() => getSubscriberInsurancePlans(beneficiaryDetails));
  const [
    selectedSubscriberBeneficiaryType,
    setSelectedSubscriberBeneficiaryType,
  ] = useState("cash");
  const formikRef = useRef(null);
  const [openCreateGurantorModal, setOpenCreateGurantorModal] = useState(false);
  const [openCreateContactModal, setOpenCreateContactModal] = useState(false);
  const [cchiData, setCchiData] = useState(null);
  const [checkEligibility, setCheckEligibility] = useState(false);
  const [openEligibilityForm, setOpenEligibilityForm] = useState(false);
  const [eligibilityResData, setEligibilityResData] = useState(null);
  const [docId, setDocId] = useState(null);
  const [showRecordSummaryModal, setShowRecordSummaryModal] = useState(false);
  const [recordSummaryData, setRecordSummaryData] = useState(null);
  const [pendingSubmitValues, setPendingSubmitValues] = useState(null);
  const [isCheckingRecord, setIsCheckingRecord] = useState(false);

  const [createPatientMutation] = useCreatePatientMutation();
  const [updatePatientMutation] = useUpdatePatientMutation();
  const [triggerGetPatientById] = useLazyGetPatientByIdQuery();
  const [triggerRecordSummary] = useGetPatientRecordSummaryMutation();

  useEffect(() => {
    const subscriberId = beneficiaryDetails?.subscriber_id;
    if (!beneficiaryDetails?.is_new_born || !subscriberId) return undefined;

    let isActive = true;

    const loadSubscriberInsurancePlans = async () => {
      try {
        const response = await triggerGetPatientById({
          patient_id: subscriberId,
        }).unwrap();
        if (!isActive || !response?.data) return;

        const subscriberPlans = getPatientInsurancePlans(response.data);
        const savedPlanId = getSubscriberInsurancePlanId(
          beneficiaryDetails?.subscriber_insurance_plan
        );
        const selectedPlan = subscriberPlans.find(
          (plan) =>
            String(plan?.insurance_plan_id || plan?.id || "") ===
              String(savedPlanId) ||
            String(plan?.his_payer_id || plan?.payer_id || "") ===
              String(savedPlanId)
        );

        setSelectedPatientInsurancePlans(subscriberPlans);
        if (selectedPlan?.insurance_plan_id || selectedPlan?.id) {
          formikRef.current?.setFieldValue(
            "subscriberInsurancePlan",
            selectedPlan?.insurance_plan_id || selectedPlan?.id
          );
          formikRef.current?.setFieldValue("insurance_plans", [selectedPlan]);
        }
        setSelectedSubscriberBeneficiaryType(
          response.data?.beneficiary_type?.toLowerCase() || "cash"
        );
      } catch (error) {
        console.error("Error loading subscriber insurance plans:", error);
      }
    };

    loadSubscriberInsurancePlans();

    return () => {
      isActive = false;
    };
  }, [
    beneficiaryDetails?.is_new_born,
    beneficiaryDetails?.subscriber_id,
    beneficiaryDetails?.subscriber_insurance_plan,
  ]);

  useEffect(() => {
    if (cchiSearchData) {
      setCchiData(cchiSearchData?.data?.Insurance?.[0]);
    }
  }, [cchiSearchData]);

  useEffect(() => {
    if (cchiData) {
      setBenificiaryType(cchiData?.BeneficiaryType == 0 ? "cash" : "insurance");
      formikRef.current?.setFieldValue(
        "benificiaryType",
        cchiData?.BeneficiaryType == 0 ? "cash" : "insurance"
      );

      formikRef.current?.setFieldValue(
        "fullName",
        cleanName(cchiData?.Name || cchiData?.NAME || "")
      );
      formikRef.current?.setFieldValue(
        "gender",
        cchiData?.Gender == 1
          ? "male"
          : cchiData?.Gender == 2
            ? "female"
            : cchiData?.Gender == 3
              ? "unknown"
              : "other" || ""
      );
      formikRef.current?.setFieldValue(
        "documentId",
        cchiData?.IdentityNumber || cchiData?.IDENTITYNUMBER || ""
      );

      if (cchiData?.BeneficiaryType != 0 && !payersData?.data) return;

      const transformInsuranceData = (selectedPlanData) => {
        const currentPayerData = payersData?.data?.find((data) =>
          payerNamesMatch(data, selectedPlanData)
        );

        if (!currentPayerData) {
          toast.error(
            t(
              "This Payer does not exist in the system. Please select an existing payer.",
            ),
          );
        }

        return {
          member_card_id: selectedPlanData?.BeneficiaryNumber || "",
          policy_number: selectedPlanData?.PolicyNumber || "",
          expiry_date: selectedPlanData?.ExpiryDate
            ? moment(selectedPlanData.ExpiryDate, [
              "YYYY-MM-DD",
              "DD/MM/YYYY",
              "DD-MM-YYYY",
              "MM-DD-YYYY",
              "MM-DD-YY",
              "DD-MM-YY",
            ]).toDate()
            : "",
          is_primary: true,
          his_payer_id: currentPayerData?.payer_id || "",
          payer_id: currentPayerData?.payer_id || "",
          payer_name: currentPayerData?.name || "--",
          relation_with_subscriber: "self",
          check_eligible: true,
          coverage_type: "EHCPOL",
          patient_share: parseFloat(selectedPlanData?.DeductibleRate) || 0,
          max_limit: parseFloat(selectedPlanData?.MaxLimit) || 0,
          network: selectedPlanData?.NetworkID || "",
          issue_date: selectedPlanData?.IssueDate
            ? moment(selectedPlanData.IssueDate, [
              "YYYY-MM-DD",
              "DD/MM/YYYY",
              "DD-MM-YYYY",
              "MM-DD-YYYY",
              "MM-DD-YY",
              "DD-MM-YY",
            ]).toDate()
            : "",
          sponsor_no: selectedPlanData?.SponsorNumber || "",
          policy_class: "",
          policy_holder: selectedPlanData?.PolicyHolder || "",
          insurance_type: "",
          insurance_status: "",
          insurance_duration: "",
          class_type: getPlanClassType(selectedPlanData),
          class_id: selectedPlanData?.ClassName || "",
          class_name: "",
        };
      };

      const newPlans =
        cchiSearchData?.data?.Insurance?.map((ins) => {
          return transformInsuranceData(ins);
        }) || [];

      const currentPlans = formikRef.current?.values?.insurance_plans || [];
      const updatedPlans = editForm ? [...currentPlans] : [];

      newPlans.forEach((newPlan) => {
        const existingPlanIndex = updatedPlans.findIndex(
          (plan) => plan?.payer_id === newPlan?.payer_id
        );

        if (existingPlanIndex !== -1) {
          updatedPlans[existingPlanIndex] = {
            ...updatedPlans[existingPlanIndex],
            ...newPlan,
          };
        } else {
          updatedPlans.push(newPlan);
        }
      });

      formikRef.current?.setFieldValue("insurance_plans", updatedPlans);

      if (
        cchiData?.IdentityNumber?.charAt(0) == 1 ||
        cchiData?.IDENTITYNUMBER?.charAt(0) == 1
      ) {
        formikRef?.current?.setFieldValue("documentType", "NI");
        formikRef?.current?.setFieldValue("residencyType", "nationalId");
      } else if (
        cchiData?.IdentityNumber?.charAt(0) == 2 ||
        cchiData?.IDENTITYNUMBER?.charAt(0) == 2
      ) {
        formikRef?.current?.setFieldValue("documentType", "PRC");
        formikRef?.current?.setFieldValue("residencyType", "iqama");
      }
    }
  }, [cchiData, cchiSearchData, payersData]);
  useEffect(() => {
    const transformInsuranceData = (selectedPlanData) => {
      const currentPayerData = payersData?.data?.find((data) =>
        data?.payer_id === selectedPlanData?.his_payer_id
      );

      if (!currentPayerData) {
        toast.error(
          t(
            "This Payer does not exist in the system. Please select an existing payer.",
          ),
        );
      }

      return {
        insurance_plan_id: selectedPlanData?.insurance_plan_id || "",
        member_card_id: selectedPlanData?.member_card_id || "",
        policy_number: selectedPlanData?.policy_number || "",
        expiry_date: selectedPlanData?.expiry_date
          ? moment(selectedPlanData.expiry_date, [
            "YYYY-MM-DD",
            "DD/MM/YYYY",
            "DD-MM-YYYY",
            "MM-DD-YYYY",
            "MM-DD-YY",
            "DD-MM-YY",
          ]).toDate()
          : "",
        is_primary: selectedPlanData?.is_primary ?? true,
        his_payer_id:
          currentPayerData?.payer_id || selectedPlanData?.his_payer_id || "",
        payer_id:
          currentPayerData?.payer_id || selectedPlanData?.payer_id || "",
        payer_name:
          currentPayerData?.name || selectedPlanData?.payer_name || "--",
        relation_with_subscriber:
          selectedPlanData?.relation_with_subscriber || "self",
        coverage_type: selectedPlanData?.coverage_type || "EHCPOL",
        patient_share: parseFloat(selectedPlanData?.patient_share) || 0,
        max_limit: parseFloat(selectedPlanData?.max_limit) || 0,
        network: selectedPlanData?.network || "",
        issue_date: selectedPlanData?.issue_date
          ? moment(selectedPlanData.issue_date, [
            "YYYY-MM-DD",
            "DD/MM/YYYY",
            "DD-MM-YYYY",
            "MM-DD-YYYY",
            "MM-DD-YY",
            "DD-MM-YY",
          ]).toDate()
          : "",
        sponsor_no: selectedPlanData?.sponsor_no || "",
        policy_holder: selectedPlanData?.policy_holder || "",
        insurance_type: selectedPlanData?.insurance_type || "",
        insurance_status: selectedPlanData?.insurance_status || "",
        insurance_duration: selectedPlanData?.insurance_duration || "",
        class_id: selectedPlanData?.class_id || "",
        class_name: selectedPlanData?.class_name || "",
        class_type: getPlanClassType(selectedPlanData),
        policy_class: selectedPlanData?.policy_class || "",
        policy_class_id: selectedPlanData?.policy_class_id || "",
        policy_class_name: selectedPlanData?.policy_class_name || "",
        policy_class_type: getPlanClassType(selectedPlanData),
        discount_percentage: selectedPlanData?.discount_percentage || 0,

      };
    };

    if (
      beneficiaryDetails?.insurance_plans?.length &&
      payersData?.data &&
      !formikRef.current?.dirty
    ) {
      formikRef.current?.setFieldValue(
        "insurance_plans",
        getBeneficiaryInsurancePlans(beneficiaryDetails).map((ins) =>
          transformInsuranceData(ins)
        )
      );
    }
  }, [beneficiaryDetails, payersData]);
  useEffect(() => {
    if (beneficiaryDetailsFromAppointment) {
      const docId = beneficiaryDetailsFromAppointment?.document_id;
      if (docId?.charAt(0) == 1) {
        formikRef?.current?.setFieldValue("documentType", "NI");
        formikRef?.current?.setFieldValue("residencyType", "nationalId");
        formikRef?.current?.setFieldValue("nationality", "Saudi");
      } else if (docId?.charAt(0) == 2) {
        formikRef?.current?.setFieldValue("documentType", "PRC");
        formikRef?.current?.setFieldValue("residencyType", "iqama");
      }
      if (beneficiaryDetailsFromAppointment?.gender) {
        formikRef?.current?.setFieldValue("gender", beneficiaryDetailsFromAppointment?.gender?.toLowerCase());
        setGender(beneficiaryDetailsFromAppointment?.gender?.toLowerCase());
      }
    }
  }, [beneficiaryDetailsFromAppointment])

  useEffect(() => {
    const inpatientValue = Boolean(
      beneficiaryDetails?.is_in_patient || isInpatientCheckInUpdate
    );
    setIsInPatient(inpatientValue);
    formikRef?.current?.setFieldValue("is_in_patient", inpatientValue);
  }, [beneficiaryDetails?.is_in_patient, isInpatientCheckInUpdate]);

  const getOccupationFormValues = (savedOccupation) => {
    const isPredefined =
      Boolean(savedOccupation) &&
      occupationOptions.some((option) => option.value === savedOccupation);

    return isPredefined
      ? { occupation: savedOccupation, occupationOther: "" }
      : savedOccupation
        ? { occupation: "others", occupationOther: savedOccupation }
        : { occupation: "unknown", occupationOther: "" };
  };

  useEffect(() => {
    const savedOccupation = beneficiaryDetails?.occupation;
    if (!savedOccupation) return;

    const { occupation, occupationOther } =
      getOccupationFormValues(savedOccupation);
    formikRef?.current?.setFieldValue("occupation", occupation);
    formikRef?.current?.setFieldValue("occupationOther", occupationOther);
  }, [beneficiaryDetails?.occupation]);

  const initialValues = {
    is_update_episode: false,
    is_new_born: beneficiaryDetails?.is_new_born || false,
    user_login: beneficiaryDetails?.user_login || isUserLogin,
    is_in_patient: Boolean(
      beneficiaryDetails?.is_in_patient || isInpatientCheckInUpdate
    ),
    benificiaryType: beneficiaryDetails?.beneficiary_type?.toLowerCase() || "",
    fullName: beneficiaryDetails?.full_name || beneficiaryDetailsFromAppointment?.full_name || "",
    dob: beneficiaryDetails?.dob
      ? moment(beneficiaryDetails?.dob, [
        "iYYYY-iMM-iDD",
        "YYYY-MM-DD",
        "DD-MM-YYYY",
        "DD/MM/YYYY",
        "MM-DD-YYYY",
        "MM-DD-YY",
        "DD-MM-YY",
      ]).toDate()
      : null,
    gender: beneficiaryDetails?.gender || beneficiaryDetailsFromAppointment?.gender?.toLowerCase() || "",
    occupation: getOccupationFormValues(beneficiaryDetails?.occupation).occupation,
    occupationOther: getOccupationFormValues(beneficiaryDetails?.occupation).occupationOther,
    religion: beneficiaryDetails?.religion || "1",
    documentType: beneficiaryDetails?.document_type || "",
    documentId:
      beneficiaryDetails?.document_id ||
      beneficiaryDetailsFromAppointment?.document_id ||
      "",
    nationality: beneficiaryDetails?.nationality || "",
    residencyType: beneficiaryDetails?.residency_type || "",
    contactNo: beneficiaryDetails?.contact || beneficiaryDetailsFromAppointment?.contact || "",
    passportNo: beneficiaryDetails?.passport_no || "",
    visaTitle: beneficiaryDetails?.visa_title || "",
    visaNumber: beneficiaryDetails?.visa_no || "",
    visaType: Number(beneficiaryDetails?.visa_type) || "",
    borderNo: beneficiaryDetails?.border_no || "",
    insuranceDuration: beneficiaryDetails?.insurance_duration || "",
    city: beneficiaryDetails?.city || "",
    address: beneficiaryDetails?.address || auth?.buisnessInfo?.city || "",
    martialStatus: beneficiaryDetails?.martial_status || "U",
    insurance_plans: getBeneficiaryInsurancePlans(beneficiaryDetails),
    subscriberRelationship: beneficiaryDetails?.subscriber_relationship || "",
    subscriberInsurancePlan: getSubscriberInsurancePlanId(
      beneficiaryDetails?.subscriber_insurance_plan
    ),
    subscriberId: getSubscriberOption(beneficiaryDetails),
    subscriberFullName: beneficiaryDetails?.subscriber_full_name || "",
    subscriberFileNo: beneficiaryDetails?.subscriber_file_no || "",
    subscriberDocumentId: beneficiaryDetails?.subscriber_document_id || "",
    subscriberRcmRef: beneficiaryDetails?.subscriber_rcm_ref || "",
    emergency_contacts: beneficiaryDetails?.emergency_contacts || [],
    guarantors: beneficiaryDetails?.guarantors || [],
    email: beneficiaryDetails?.email || "",
    password: beneficiaryDetails?.password || "",
    old_file_no: beneficiaryDetails?.old_file_no || "",
    file_no: beneficiaryDetails?.file_no || "",
  };

  useEffect(() => {
    formikRef?.current?.setFieldValue("benificiaryType", benificiaryType);
    if (benificiaryType != "insurance") {
      setCheckEligibility(false);
    }
  }, [benificiaryType]);

  const selectSubscriberInsurancePlan = (
    plan,
    setFieldValue,
    subscriberType = selectedSubscriberBeneficiaryType
  ) => {
    if (!plan) {
      setFieldValue("subscriberInsurancePlan", "");
      setFieldValue("insurance_plans", []);
      setBenificiaryType("cash");
      setFieldValue("benificiaryType", "cash");
      return;
    }

    const insurancePlanId = plan?.insurance_plan_id || plan?.id || "";
    const payerId = plan?.his_payer_id || plan?.payer_id || "";
    const payer = payersData?.data?.find(
      (payerData) => payerData?.payer_id === payerId
    );
    const normalizedPlan = normalizeInsurancePlan({
      ...plan,
      insurance_plan_id: insurancePlanId,
      payer_id: payerId,
      his_payer_id: payerId,
      payer_name: payer?.name || plan?.payer_name || getPolicyHolder(plan) || "",
    });

    setFieldValue("subscriberInsurancePlan", insurancePlanId);
    setBenificiaryType(subscriberType);
    setFieldValue("benificiaryType", subscriberType);
    setFieldValue("insurance_plans", [normalizedPlan]);
  };

  const handleNewBornChange = (value) => {
    setIsNewBorn(value);
    formikRef.current?.setFieldValue("is_new_born", value);
    if (formikRef.current?.values?.documentId === "0000000000") {
      formikRef.current?.setFieldValue("documentId", "");
    }
  };

  const checkPatientRecordSummary = async (documentId, fileNumber) => {
    try {
      setIsCheckingRecord(true);
      const payload = {};
      
      if (documentId) {
        payload.document_id = documentId;
      }
      if (fileNumber) {
        payload.file_number = fileNumber;
      }

      const response = await triggerRecordSummary(payload).unwrap();
      
      if (response?.data) {
        const data = response.data;
        const hasActiveRecords = 
          (data.patient && data.patient != null)

        if (hasActiveRecords) {
          setRecordSummaryData(data);
          setShowRecordSummaryModal(true);
          return true;
        }
      }
      return false;
    } catch (error) {
      if (error?.response?.status === 404) {
        return false;
      }
      console.error("Error checking patient record:", error);
      return false;
    } finally {
      setIsCheckingRecord(false);
    }
  };

  useEffect(() => {
    if (
      isHaveGetPayerPermission &&
      !payersData?.length &&
      benificiaryType != "cash"
    ) {
      dispatch(getPayersList({ category: benificiaryType }));
    }
  }, [benificiaryType]);

  const getSelectedSubscriberPlan = (values) => {
    const selectedPlanValue = getSubscriberInsurancePlanId(
      values?.subscriberInsurancePlan
    );
    const availablePlans = [
      ...(selectedPatientInsurancePlans || []),
      ...(values?.insurance_plans || []),
    ];

    return availablePlans.find(
      (plan) =>
        String(plan?.insurance_plan_id || plan?.id || "") ===
          String(selectedPlanValue) ||
        String(plan?.his_payer_id || plan?.payer_id || "") ===
          String(selectedPlanValue)
    );
  };

  const handleSubmit = async (values) => {
    const selectedSubscriberPlan = isNewBorn
      ? getSelectedSubscriberPlan(values)
      : null;
    const insurancePlans = isNewBorn
      ? selectedSubscriberPlan
        ? [normalizeInsurancePlan(selectedSubscriberPlan)]
        : []
      : (values?.insurance_plans || []).map(normalizeInsurancePlan);

    if (values?.benificiaryType != "cash" && !insurancePlans.length) {
      toast.error(t("Please add at least one insurance plan"));
      return;
    }

    if (values?.benificiaryType != "cash") {
      const isPayerIdMissing = insurancePlans.length
        ? insurancePlans.some((plan) => !plan?.his_payer_id)
        : true;
      if (isPayerIdMissing) {
        toast.error(t("Select an insurance plan that has a valid payer"));
        return;
      }

      const invalidPlanIndex = insurancePlans.findIndex(
        (plan) =>
          getMissingInsurancePlanFields(plan, values?.benificiaryType).length > 0
      );
      if (invalidPlanIndex !== -1) {
        const missingFields = getMissingInsurancePlanFields(
          insurancePlans[invalidPlanIndex],
          values?.benificiaryType
        );
        toast.error(
          t("Insurance plan") +
            ` ${invalidPlanIndex + 1}: ` +
            missingFields.map((field) => t(field)).join(", ") +
            " " +
            t("is required")
        );
        return;
      }
    }

    if (!beneficiaryDetails && !isNewBorn) {
      const hasRecords = await checkPatientRecordSummary(
        values?.documentId,
        values?.file_no
      );
      
      if (hasRecords) {
        setPendingSubmitValues(values);
        return;
      }
    }

    await proceedWithSubmit(values);
  };

  const proceedWithSubmit = async (values) => {
    const { is_update_episode, occupationOther, ...patientValues } = values;
    const selectedSubscriberPlanValue = getSubscriberInsurancePlanId(
      values?.subscriberInsurancePlan
    );
    const selectedSubscriberPlan = getSelectedSubscriberPlan(values);
    const subscriberInsurancePlan =
      selectedSubscriberPlan?.insurance_plan_id ||
      selectedSubscriberPlan?.id ||
      selectedSubscriberPlanValue;
    const insurancePlansForPayload = isNewBorn
      ? selectedSubscriberPlan
        ? [normalizeInsurancePlan(selectedSubscriberPlan)]
        : []
      : (values?.insurance_plans || []).map(normalizeInsurancePlan);

    let payload = {
      ...patientValues,
      full_name: values?.fullName,
      contact_number: String(values?.contactNo),
      email: values?.user_login ? values?.email : "",
      password: values?.user_login ? values?.password : "",
      contact: String(values?.contactNo),
      beneficiary_type: values?.benificiaryType,
      old_file_no: Number(values?.old_file_no),
      dob: moment(values?.dob).format("YYYY-MM-DD"),
      document_id: values?.documentId,
      document_type: values?.documentType,
      insurance_duration: values?.insuranceDuration,
      martial_status: values?.martialStatus,
      occupation:
        values?.occupation === "others"
          ? occupationOther?.trim() || "unknown"
          : values?.occupation,
      passport_no: values?.passportNo,
      residency_type: values?.residencyType,
      visa_title: values?.visaTitle,
      visa_type: String(values?.visaType),
      visa_no: values?.visaNumber,
      is_new_born: isNewBorn,
      border_no: values?.borderNo,
      insurance_plans:
        values?.benificiaryType != "cash"
          ? insurancePlansForPayload.map((plan) => ({
            ...plan,
            max_limit: parseInt(plan.max_limit),
            patient_share: String(plan.patient_share),
            class_name: plan?.class_name,
            class_id: plan?.class_id,
            class_type: getPlanClassType(plan),
            insurance_plan_id: plan?.insurance_plan_id || "",
            policy_class_name: plan?.class_name,
            policy_class_id: plan?.class_id,
            policy_class_type: getPlanClassType(plan),
          }))
          : [],
    };

    if (isNewBorn) {
      payload.subscriber_id = values?.subscriberId?.value;
      payload.subscriber_relationship = values?.subscriberRelationship;
      payload.subscriber_insurance_plan = [subscriberInsurancePlan];
      payload.subscriber_full_name = values?.subscriberFullName || "";
      payload.subscriber_file_no = values?.subscriberFileNo || "";
      payload.subscriber_document_id = values?.subscriberDocumentId || "";
      payload.subscriber_rcm_ref = values?.subscriberRcmRef || "";
      payload.subscriber_nationality = values?.nationality || "";
      payload.subscriber_city = values?.city || "";
      payload.subscriber_address = values?.address || "";
      payload.subscriber_religion = values?.religion || "";
    }

    if (isUserLogin) {
      payload.user_login = true;
      payload.email = values?.email;
      payload.password = values?.password;
      payload.role = "patient";
    }

    if (!beneficiaryDetails || editForm) {
      payload.is_in_patient = isInPatient;
    }

    if (checkEligibility) {
      setEligibilityLoading(true);
    } else {
      setIsLoading(true);
    }
    if (isNewBorn) {
      payload.subscriberId = values?.subscriberId?.value || "";
      payload.subscriberRelationship = values?.subscriberRelationship || "";
      payload.subscriberInsurancePlan = [subscriberInsurancePlan];
    }


    try {
      if (beneficiaryDetails) {
        payload.patient_id = beneficiaryDetails?.patient_id;
        payload.rcm_ref = beneficiaryDetails?.rcm_ref;
        payload.is_update_episode = Boolean(is_update_episode);
        const elIds = payload?.insurance_plans
          ?.filter((el) => el?.check_eligible === true)
          .map((el) => el.his_payer_id);

        const response = await updatePatientMutation(payload).unwrap();
        toast.success(response?.message || "Patient updated successfully");

        if (checkEligibility) {
          setCheckEligibility(false);
          const plansToFilter = response?.patient?.insurance_plans || [];
          const payloads = plansToFilter
            ?.filter((el) => (checkEligibility ? true : elIds.includes(el?.his_payer_id)))
            .map((data) => ({
              payer_id: data?.payer_id,
              rcm_ref: response?.patient?.rcm_ref,
              insurance_plan_id: data?.insurance_plan_id,
              is_new_born:
                payload?.is_new_born ??
                response?.patient?.is_new_born ??
                false,
            }));

          const results = await Promise.all(
            payloads.map((data) =>
              handleCheckPatientElibility(
                data,
                () => {},
                () => {}
              )
            )
          );
          const validResults = results.filter((r) => r);
          setEligibilityResData(validResults);
          setOpenEligibilityForm(true);
        } else {
          fromVisit
            ? navigate(
                returnDetail?.is_without_slot == true
                  ? "/create-visit-without-slot"
                  : "/create-visit",
                { state: returnDetail }
              )
            : navigate("/beneficiary", {
                state: { prevPayload: getBeneficiariesPayload },
              });
        }
      } else {
        const response = await createPatientMutation(payload).unwrap();
        toast.success(response?.message || "Patient created successfully");
        setDocId(response?.patient?.document_id);

        if (checkEligibility) {
          setCheckEligibility(false);
          const payloads = response?.patient?.insurance_plans?.map(
            (data) => ({
              payer_id: data?.payer_id,
              rcm_ref: response?.patient?.rcm_ref,
              insurance_plan_id: data?.insurance_plan_id,
              is_new_born:
                payload?.is_new_born ??
                response?.patient?.is_new_born ??
                false,
            })
          );

          const results = await Promise.all(
            payloads.map((data) =>
              handleCheckPatientElibility(
                data,
                () => {},
                () => {}
              )
            )
          );
          const validResults = results.filter((r) => r);
          setEligibilityResData(validResults);
          setOpenEligibilityForm(true);
        } else {
          fromVisit
            ? navigate(
                returnDetail?.is_without_slot == true
                  ? "/create-visit-without-slot"
                  : "/create-visit",
                {
                  state: {
                    ...returnDetail,
                    doc_id: response?.patient?.document_id,
                  },
                }
              )
            : navigate("/beneficiary");
        }
      }
    } catch (error) {
      setCheckEligibility(false);
    } finally {
      setIsLoading(false);
      setEligibilityLoading(false);
    }
  };

  const handleConfirmReplacement = async () => {
    setShowRecordSummaryModal(false);
    if (pendingSubmitValues) {
      await proceedWithSubmit(pendingSubmitValues);
      setPendingSubmitValues(null);
    }
  };

  const handleCancelReplacement = () => {
    setShowRecordSummaryModal(false);
    setRecordSummaryData(null);
    setPendingSubmitValues(null);
    setIsLoading(false);
    setEligibilityLoading(false);
  };

  return (
    <React.Fragment>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center w-full mt-2">
        <CardHeading
          title={t("PersonalData")}
          Icon={BsPersonBoundingBox}
          iconSize={15}
        />
        <div className="flex flex-wrap items-center gap-3">
          <CustomButton
            type="button"
            onClick={() => {
              setOpenCreateGurantorModal(true);
            }}
            label={t("AddGuarantor")}
            icon={<FaPlus size={12} className="me-2" />}
            buttonType="primary"
            className="mt-2"
          />
          <CustomButton
            type="button"
            onClick={() => {
              setOpenCreateContactModal(true);
            }}
            label={t("AddEmergencyContact")}
            icon={<FaPlus size={12} className="me-2" />}
            buttonType="primary"
            className="mt-2"
          />
          <SwitchInput
            parentClass="!w-fit"
            value={isNewBorn}
            label={t("NewBorn?")}
            handleChange={(e) => handleNewBornChange(e?.target?.value)}
          />
          <SwitchInput
            parentClass="!w-fit"
            value={isUserLogin}
            label={t("UserLogin?")}
            handleChange={(e) => {
              setIsUserLogin(e?.target?.value);
              formikRef?.current?.setFieldValue("user_login", e?.target?.value);
            }}
          />
          {shouldShowInPatientSwitch && (
            <SwitchInput
              parentClass="!w-fit"
              value={isInPatient}
              label={t("In Patient?")}
              handleChange={(e) => {
                setIsInPatient(e?.target?.value);
                formikRef?.current?.setFieldValue("is_in_patient", e?.target?.value);
              }}
            />
          )}
          {beneficiaryDetails && (
            <SwitchInput
              parentClass="!w-fit"
              label={t("Update Episode?")}
              value={isUpdateEpisode}
              handleChange={(e) => {
                const enabled = Boolean(e.value);
                setIsUpdateEpisode(enabled);
                formikRef.current?.setFieldValue("is_update_episode", enabled);
              }}
              disabled={isLoading || eligibilityLoading || isCheckingRecord}
            />
          )}
        </div>
      </div>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={BeneficiaryValidationSchema(t)}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <Form className="w-full mt-4 md:mt-6">
            <div className="w-full grid grid-cols-12 gap-x-3 gap-y-1 ">
              <div className="col-span-12 sm:col-span-6 md:col-span-8 lg:col-span-6 xl:col-span-4">
                <label className="input-label block !mb-2">
                  {t("BeneficiaryType")}
                </label>
                <SwitchButtons
                  labels={[
                    { name: t("Cash"), value: "cash" },
                    { name: t("Insurance"), value: "insurance" },
                    { name: t("Corporate"), value: "corporate" },
                    { name: t("Government"), value: "government" },
                  ]}
                  setActiveState={setBenificiaryType}
                  activeState={benificiaryType}
                />
              </div>

              <TextInput
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("FullName*")}
                placeholder={t("Enterfullname")}
                name="fullName"
              />
              <DropdownField
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("SelectGender*")}
                placeholder={t("Selectgender")}
                onChange={(e) => {
                  const value = e?.target?.value;
                  setGender(value);
                  props?.setFieldValue("gender", value);
                }}
                name="gender"
                options={genderOptions}
                optionLabel={i18n.language == "en" ? "label" : "labelAr"}
                optionValue={"value"}
              />

              <div className="col-span-12 sm:col-span-12 md:col-span-8 lg:col-span-6 xl:col-span-4 mb-1 bg-blue-50/30 rounded-lg ring-1 ring-gray-300 bg-gray-100 p-1 px-1.5">
                <div className="flex items-center justify-between w-full mb-1">
                  <div className="flex items-baseline gap-1.5">
                    <label className="input-label">
                      {t("DateOfBirth*")} <span className="font-semibold text-gray-500">({t("Gregorian")})</span>
                    </label>
                    <span className="text-[11px] text-gray-500 lowercase">(dd/mm/yyyy)</span>
                  </div>

                  <div className="flex items-baseline gap-1.5">
                    <label className="text-sm font-bold text-gray-500">
                      ({t("Hijri")})
                    </label>
                    <span className="text-[11px] text-gray-500 lowercase">(dd/mm/yyyy)</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <DatePicker
                    className="w-full !mb-0"
                    placeholder={t("Gregorian")}
                    name="dob"
                  />
                  <HijriDatePicker
                    className="w-full !mb-0"
                    placeholder={t("Hijri")}
                    name="dob"
                    format="DD/MM/YYYY"
                    locale={i18n?.language}
                  />
                </div>
              </div>

              {editForm && (
                <TextInput
                  parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                  label={t("MRN")}
                  name="file_no"
                  readOnly
                />
              )}
              <DropdownField
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("MartialStatus*")}
                placeholder={t("Selectmartialstatus")}
                name="martialStatus"
                options={maritalStatusOptions.map((opt) => ({
                  ...opt,
                  label: t(opt.label),
                }))}
              />
              <DropdownField
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("Occupation*")}
                placeholder={t("Selectoccupation")}
                name="occupation"
                options={occupationOptions.map((opt) => ({
                  ...opt,
                  label: t(opt.label),
                }))}
              />
              {props.values?.occupation === "others" && (
                <TextInput
                  parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                  label={`${t("Enter occupation")}*`}
                  placeholder={t("Enter occupation")}
                  name="occupationOther"
                />
              )}
              <DropdownField
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("Religion*")}
                placeholder={t("Selectreligion")}
                name="religion"
                options={religionOptions.map((opt) => ({
                  ...opt,
                  label: t(opt.label),
                }))}
              />
              <DropdownField
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("DocumentType*")}
                placeholder={t("Selectdocumenttype")}
                name="documentType"
                options={documentTypeOptions.map((opt) => ({
                  ...opt,
                  label: t(opt.label),
                }))}
              />
              <TextInput
                type={'text'}
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("DocumentID*")}
                placeholder={t("Enterdocumentid")}
                name="documentId"
                onChange={(e) => {
                  props?.setFieldValue("documentId", e.target.value);
                  if (e?.target?.value?.charAt(0) == 1) {
                    props?.setFieldValue("documentType", "NI");
                    props?.setFieldValue("residencyType", "nationalId");
                    props.setFieldValue("nationality", "Saudi");
                  } else if (e?.target?.value?.charAt(0) == 2) {
                    props?.setFieldValue("documentType", "PRC");
                    props?.setFieldValue("residencyType", "iqama");
                    props.setFieldValue("nationality", "");
                  }
                  // else {
                  //   props?.setFieldValue("documentType", "");
                  //   props?.setFieldValue("residencyType", "");
                  //   props.setFieldValue("nationality", "");
                  // }
                }}
              />
              <DropdownField
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("Nationality*")}
                placeholder={t("Selectnationality")}
                name="nationality"
                onChange={(e) => {
                  if (
                    e?.target?.value === "Saudi" &&
                    props?.values?.documentId?.charAt(0) === "2"
                  ) {
                    toast.error("Nationality Cannot Be Saudi");
                  } else {
                    props?.setFieldValue("nationality", e?.target?.value);
                  }
                }}
                filter={true}
                options={nationalitiesOptions}
                optionValue={"value"}
                optionLabel={i18n.language == "en" ? "label_en" : "label_ar"}
              />
              <DropdownField
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("ResidencyType*")}
                placeholder={t("Selectresidencytype")}
                name="residencyType"
                options={residencyTypesOptions.map((opt) => ({
                  ...opt,
                  label: t(opt.label),
                }))}
              />
              <TextInput
                type="number"
                step="any"
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("ContactNumber*")}
                placeholder={t("Entercontactnumber")}
                name="contactNo"
              />
              <TextInput
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("PassportNumber")}
                placeholder={t("Enterpassportnumber")}
                name="passportNo"
              />
              <TextInput
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("VisaTitle")}
                placeholder={t("Entervisatitle")}
                name="visaTitle"
              />
              <TextInput
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("VisaNumber")}
                placeholder={t("Entervisanumber")}
                name="visaNumber"
              />
              <DropdownField
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("VisaType")}
                placeholder={t("Selectvisatype")}
                name="visaType"
                options={visaTypeOptions.map((opt) => ({
                  ...opt,
                  label: t(opt.label),
                }))}
              />
              <TextInput
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("BorderNumber")}
                placeholder={t("Enterbordernumber")}
                name="borderNo"
              />
              <TextInput
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("InsuranceDuration")}
                placeholder={t("Enterinsuranceduration")}
                name="insuranceDuration"
              />
              <DropdownField
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("SelectCity")}
                placeholder={t("Selectcity")}
                name="city"
                filter={true}
                options={citiesOptions.map((opt) => ({
                  ...opt,
                  label: t(opt.label),
                }))}
              />
              <TextInput
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("Address*")}
                placeholder={t("Enteraddress")}
                name="address"
              />
              <TextInput
                parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3 xl:col-span-2"
                label={t("Old MRN")}
                placeholder={t("Enter Old MRN")}
                name="old_file_no"
              />
            </div>
            {isNewBorn && (
              <div className="w-full grid grid-cols-12 gap-x-3 gap-y-1">
                <div className="col-span-12 mt-2 mb-2">
                  <CardHeading
                    title={t("NewBorn")}
                    Icon={LuBaby}
                    iconSize={20}
                  />
                </div>
                <AutoCompleteField
                  parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3"
                  label={t("SelectPatient")}
                  placeholder={t("Searchpatient")}
                  name="subscriberId"
                  optionsFormat={patientOptionsFormat}
                  forceSelection={false}
                  preserveValueOnClear
                  onSelect={async (e) => {
                    let subscriber = e?.value;

                    try {
                      const response = await triggerGetPatientById({
                        patient_id: subscriber?.value,
                      }).unwrap();
                      if (response?.data) {
                        subscriber = {
                          ...subscriber,
                          ...response.data,
                          value:
                            response.data?.patient_id || subscriber?.value,
                        };
                      }
                    } catch (error) {
                      console.error(
                        "Error loading subscriber insurance plans:",
                        error
                      );
                    }

                    const subscriberPlans =
                      getPatientInsurancePlans(subscriber);
                    const subscriberType =
                      subscriber?.beneficiary_type?.toLowerCase() || "cash";

                    setSelectedPatientInsurancePlans(subscriberPlans);
                    setSelectedSubscriberBeneficiaryType(subscriberType);
                    setBenificiaryType(subscriberType);
                    props.setFieldValue("benificiaryType", subscriberType);
                    props.setFieldValue(
                      "subscriberFullName",
                      subscriber?.full_name || ""
                    );
                    props.setFieldValue(
                      "subscriberFileNo",
                      subscriber?.file_no || ""
                    );
                    props.setFieldValue(
                      "subscriberDocumentId",
                      subscriber?.document_id || ""
                    );
                    props.setFieldValue(
                      "subscriberRcmRef",
                      subscriber?.rcm_ref || ""
                    );
                    props.setFieldValue(
                      "fullName",
                      getBabyName(subscriber?.full_name)
                    );
                    props.setFieldValue(
                      "nationality",
                      subscriber?.nationality || ""
                    );
                    props.setFieldValue("city", subscriber?.city || "");
                    props.setFieldValue("address", subscriber?.address || "");
                    props.setFieldValue("religion", subscriber?.religion || "");

                    if (
                      subscriberType === "insurance" &&
                      subscriberPlans.length
                    ) {
                      selectSubscriberInsurancePlan(
                        subscriberPlans[0],
                        props.setFieldValue,
                        subscriberType
                      );
                    } else {
                      props.setFieldValue("subscriberInsurancePlan", "");
                      props.setFieldValue("insurance_plans", []);
                    }
                  }}
                  onClear={() => {
                    props.setFieldValue("subscriberId", "");
                    props.setFieldValue("subscriberFullName", "");
                    props.setFieldValue("subscriberFileNo", "");
                    props.setFieldValue("subscriberDocumentId", "");
                    props.setFieldValue("subscriberRcmRef", "");
                    props.setFieldValue("subscriberInsurancePlan", "");
                    props.setFieldValue("insurance_plans", []);
                    props.setFieldValue("fullName", "");
                    setBenificiaryType("cash");
                    props.setFieldValue("benificiaryType", "cash");
                    setSelectedPatientInsurancePlans([]);
                    setSelectedSubscriberBeneficiaryType("cash");
                  }}
                  fieldUrl="/v3/patient/search"
                />
                <DropdownField
                  parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3"
                  label={t("SubscriberRelationship")}
                  placeholder={t("Selectsubscriberrelationship")}
                  name="subscriberRelationship"
                  options={subscriberRelationsOptions.map((opt) => ({
                    ...opt,
                    label: t(opt.label),
                  }))}
                />
                <DropdownField
                  parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3"
                  label={t("SubscriberInsurancePlan")}
                  placeholder={t("Selectsubscriberinsuranceplan")}
                  name="subscriberInsurancePlan"
                  options={patientInsurancePlanOptions(
                    selectedPatientInsurancePlans
                  )}
                  showClear
                  onChange={(e) => {
                    const selectedPayerId = e?.target?.value;
                    const selectedPlan = selectedPatientInsurancePlans.find(
                      (plan) =>
                        String(plan?.insurance_plan_id || plan?.id || "") ===
                        String(selectedPayerId)
                    );
                    selectSubscriberInsurancePlan(
                      selectedPlan,
                      props.setFieldValue
                    );
                  }}
                />
              </div>
            )}

            {props.values?.user_login && (
              <div className="w-full grid grid-cols-12 gap-x-3 gap-y-1">
                <div className="col-span-12 mt-2 mb-2">
                  <CardHeading
                    title={t("Patient Login")}
                    Icon={FaUserLock}
                    iconSize={20}
                  />
                </div>
                <TextInput
                  parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3"
                  label={t("email*")}
                  placeholder={t("enter your email")}
                  name="email"
                />
                <PasswordField
                  parentClass="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3"
                  label={t("Password")}
                  name={"password"}
                  type="password"
                  isIcon={true}
                  placeholder={t("EnterPassword")}
                  className="!p-1.5"
                  toggleMask
                />
              </div>
            )}
            {props?.values?.guarantors?.length > 0 ? (
              <BeneFiciaryGuarantorForm
                setOpenCreateGurantorModal={setOpenCreateGurantorModal}
                openCreateGurantorModal={openCreateGurantorModal}
                formikRef={formikRef}
                formValues={formikRef?.current?.values}
              />
            ) : (
              openCreateGurantorModal && (
                <BeneFiciaryGuarantorForm
                  setOpenCreateGurantorModal={setOpenCreateGurantorModal}
                  openCreateGurantorModal={openCreateGurantorModal}
                  formikRef={formikRef}
                  formValues={formikRef?.current?.values}
                />
              )
            )}

            {props?.values?.emergency_contacts?.length > 0 ? (
              <BeneficiaryEmergencyContact
                setOpenCreateContactModal={setOpenCreateContactModal}
                openCreateContactModal={openCreateContactModal}
                formikRef={formikRef}
                formValues={formikRef?.current?.values}
              />
            ) : (
              openCreateContactModal && (
                <BeneficiaryEmergencyContact
                  setOpenCreateContactModal={setOpenCreateContactModal}
                  openCreateContactModal={openCreateContactModal}
                  formikRef={formikRef}
                  formValues={formikRef?.current?.values}
                />
              )
            )}

            {benificiaryType != "cash" ? (
              <InsurancePlansForm cchiData={cchiData} />
            ) : (
              ""
            )}
            <div className="w-full flex items-center justify-end gap-3 mt-2">
              {(hasPermission("patient", "create") || hasPermission("patient", "update")) && (
                <CustomButton
                  disabled={isLoading || eligibilityLoading || isCheckingRecord}
                  type="submit"
                  label={t("SaveBeneficiary")}
                  loading={isLoading || isCheckingRecord}
                  icon={<FaCheckDouble size={12} className="me-2" />}
                  buttonType="primary"
                />
              )}
              {(hasPermission("patient", "create") || hasPermission("patient", "update")) &&
                props.values?.benificiaryType == "insurance" && (
                  <CustomButton
                    disabled={eligibilityLoading || isLoading || isCheckingRecord}
                    onClick={() => {
                      setCheckEligibility(true);
                      props.handleSubmit();
                    }}
                    label={t("Save & Check Eligibility")}
                    loading={eligibilityLoading || isCheckingRecord}
                    icon={<FaCheckDouble size={12} className="me-2" />}
                    buttonType="primary"
                  />
                )}
            </div>
          </Form>
        )}
      </Formik>
      <ReusableModal
        className="w-full md:!w-[90%] xl:!w-[80%]"
        position="top"
        HeaderIcon={FaShield}
        title={t("Eligibility Response")}
        isVisible={openEligibilityForm}
        onClose={() => {
          setEligibilityResData(null);
          setOpenEligibilityForm(false);
          if (navigate) {
            fromVisit
              ? navigate(
                returnDetail?.is_without_slot == true
                  ? "/create-visit-without-slot"
                  : "/create-visit",
                {
                  state: returnDetail?.doc_id
                    ? returnDetail
                    : { ...returnDetail, doc_id: docId },
                }
              )
              : navigate("/beneficiary");
          }
        }}
      >
        <EligibilityDetailComp data={eligibilityResData} />
      </ReusableModal>

      <PatientRecordSummaryModal
        isVisible={showRecordSummaryModal}
        onClose={handleCancelReplacement}
        recordData={recordSummaryData}
        onConfirm={handleConfirmReplacement}
        isLoading={isLoading || eligibilityLoading}
      />
    </React.Fragment >
  );
};

export default BeneficiaryPersonalDataForm;
